import {
  bindSurfaceObjects,
  type SurfaceObject,
  type SurfaceObjectSource,
} from '@affine/core/modules/streamlain/copilot/surface-objects';
import { WorkspaceViewContextService } from '@affine/core/modules/streamlain/copilot/workspace-view-context';
import { ViewService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { I18n } from '@affine/i18n';
import { useServiceOptional } from '@toeverything/infra';
import { type RefObject, useLayoutEffect, useRef } from 'react';

const PAGE_FRAME_ATTRIBUTE = 'data-pdf-page-frame-index';
const TEXT_LAYER_SELECTOR = '[data-pdf-text-layer="true"]';
/** A stretch the viewer drew a stored highlight or a comment over; a click on it opens what it offers. */
const MARK_SELECTOR = '[data-highlight="true"], [data-comment="true"]';
/** The id of a mark: its page, `:mark:` and its place among the marks drawn on that page. */
const MARK_ID = /^(\d+):mark:(\d+)$/u;

/** The glyphs of a page whose middle lies in a box, in the order the page lists them. */
function textIn(frame: Element, box: DOMRect): string {
  let text = '';
  for (const glyph of frame.querySelectorAll(`${TEXT_LAYER_SELECTOR} span`)) {
    const rect = glyph.getBoundingClientRect();
    const x = rect.x + rect.width / 2;
    const y = rect.y + rect.height / 2;
    if (x >= box.left && x <= box.right && y >= box.top && y <= box.bottom)
      text += glyph.textContent ?? '';
  }
  return text;
}

/**
 * The pages of a PDF as a drawn surface: every page the viewer has laid out
 * (the visible window of its virtual list), with the box it is drawn in now,
 * and the marks drawn on it.
 */
export function pdfSurfaceObjects(
  readRoot: () => HTMLElement | null,
  currentPage: () => number,
  pageCount: number,
  label: () => string
): SurfaceObjectSource {
  const project = (frame: Element): SurfaceObject | null => {
    const id = frame.getAttribute(PAGE_FRAME_ATTRIBUTE);
    const index = Number(id);
    if (id === null || !Number.isInteger(index)) return null;
    const rect = frame.getBoundingClientRect();
    return {
      id,
      role: 'page',
      label: I18n.t('com.streamlain.pdf.searchCoverage.review.pagePosition', {
        current: index + 1,
        total: pageCount,
      }),
      // The glyphs in the order the page lists them; cut to the view's budget by the context.
      text: frame.querySelector(TEXT_LAYER_SELECTOR)?.textContent ?? undefined,
      selected: index === currentPage(),
      box: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
    };
  };
  /** The marks drawn on a page: every highlighted or commented stretch, with the text under it. */
  const marks = (frame: Element): SurfaceObject[] => {
    const page = frame.getAttribute(PAGE_FRAME_ATTRIBUTE);
    return Array.from(frame.querySelectorAll(MARK_SELECTOR), (mark, index) => {
      const rect = mark.getBoundingClientRect();
      const role = mark.getAttribute('data-kind') ?? 'highlight';
      const text = textIn(frame, rect).trim();
      return {
        id: `${page}:mark:${index}`,
        role,
        label: text || role,
        ...(mark.getAttribute('data-active') === 'true'
          ? { selected: true }
          : {}),
        box: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
      };
    });
  };
  const root = () => {
    const element = readRoot();
    return element?.isConnected ? element : null;
  };
  const frames = () =>
    root()?.querySelectorAll(`[${PAGE_FRAME_ATTRIBUTE}]`) ?? [];
  return {
    role: 'document',
    get label() {
      return label();
    },
    root,
    *objects() {
      for (const frame of frames()) {
        const object = project(frame);
        if (!object) continue;
        yield object;
        yield* marks(frame);
      }
    },
    object: id => {
      const mark = MARK_ID.exec(id);
      if (mark) {
        for (const frame of frames())
          if (frame.getAttribute(PAGE_FRAME_ATTRIBUTE) === mark[1])
            return marks(frame)[Number(mark[2])] ?? null;
        return null;
      }
      for (const frame of frames())
        if (frame.getAttribute(PAGE_FRAME_ATTRIBUTE) === id)
          return project(frame);
      return null;
    },
    // The page's text as the viewer lays it out: where a pointer selects it.
    readableTexts: id => {
      for (const frame of frames())
        if (frame.getAttribute(PAGE_FRAME_ATTRIBUTE) === id)
          return Array.from(frame.querySelectorAll(TEXT_LAYER_SELECTOR));
      return [];
    },
  };
}

/** The PDF in the view the AI sees: its laid-out pages, read at request time. */
export function usePdfViewContext({
  attachmentId,
  name,
  pageCount,
  viewerRef,
  cursorRef,
}: {
  attachmentId: string;
  name: string;
  pageCount: number;
  viewerRef: RefObject<HTMLElement | null>;
  cursorRef: RefObject<number>;
}): void {
  const context = useServiceOptional(WorkspaceViewContextService);
  const view = useServiceOptional(ViewService)?.view;
  const workspaceId = useServiceOptional(WorkspaceService)?.workspace.id;
  const nameRef = useRef(name);
  nameRef.current = name;
  useLayoutEffect(() => {
    if (!context || !view || !workspaceId) return;
    return bindSurfaceObjects(
      context,
      view,
      workspaceId,
      `pdf:${attachmentId}`,
      pdfSurfaceObjects(
        () => viewerRef.current,
        () => cursorRef.current,
        pageCount,
        () => nameRef.current
      )
    );
  }, [
    attachmentId,
    context,
    cursorRef,
    pageCount,
    view,
    viewerRef,
    workspaceId,
  ]);
}
